/**
 * The task fold: whether a task stands open, which board it is pinned to, and what it keeps handled.
 *
 * A task is a fact, one row, never updated; its pins and its acknowledgements are logs beside it, and everything here
 * is interpretation, decided when they are read. A task with an expiry is open until it and expired after it, whatever
 * is acknowledged; one without is done once its own acknowledgement stack stands, and open otherwise, so an undo
 * reopens it. Nothing is written when a task expires: the reader's clock decides it. The latest pin is the one in
 * force. An open task keeps every item it names handled; a done or expired one keeps nothing, and the item falls back
 * to its own acknowledgement log. A task about a driver, by membership, names no item, so it hands the driver to a
 * board without handling the driver's waiting messages. Browser-safe and free of every dependency but Luxon, so the
 * board and any server-side count fold tasks the same way.
 */

import { DateTime } from "luxon";
import type { AcknowledgementRecord } from "./acknowledgement";
import { byLogOrder, foldAcknowledgements, isStandingAcknowledged } from "./acknowledgement";

/**
 * One subject of a task: what it is about, and the driver that leads to.
 *
 * The driver is the target itself for `membership` and the driver the server copied onto the row otherwise; null when
 * there is none.
 */
export type TaskSubjectRef = {
  subject:
    | "membership"
    | "feed_item"
    | "stop_event"
    | "form_submission"
    | "hos_clock_violation"
    | "route_assignment_event";
  targetId: string;
  workspaceMembershipId: string | null;
};

/**
 * One task, the wire row as the store holds it.
 */
export type TaskRecord = {
  id: string;
  workspaceId: string;
  note: string | null;
  /** The creator's membership id; null once that membership is gone. */
  createdBy: string | null;
  subjects: readonly TaskSubjectRef[];
  /**
   * When the task closes unless acknowledged first, ISO; null when it never expires.
   *
   * Absent on a row the browser stored before tasks carried it, which reads as null.
   */
  expiresAt?: string | null;
  /** The server's instant for the row, ISO. */
  createdAt: string;
};

/**
 * One pin placing a task on a board, the wire row as the store holds it.
 */
export type TaskPinBoardRecord = {
  id: string;
  taskId: string;
  /** The membership that pinned it; null once that membership is gone. */
  actorWorkspaceMembershipId: string | null;
  /** The key of a view in the workspace's `web.dispatch.views`. */
  viewId: string;
  /** The server's instant for the row, ISO: the order pins are read in. */
  createdAt: string;
};

/**
 * An open task with the pin in force, or `null` when it has none.
 */
export type OpenTask = { task: TaskRecord; pin: TaskPinBoardRecord | null };

/**
 * The key {@link isHandledByTask} reads a subject's open tasks under.
 *
 * @param subject - The subject kind; a message and a driver never share a key, whatever their ids.
 * @param targetId - The target's id within that kind.
 */
export function taskSubjectKey(subject: TaskSubjectRef["subject"], targetId: string): string {
  return `${subject}:${targetId}`;
}

/**
 * Where a task stands: open on its board, done by an acknowledgement, or closed by its expiry.
 */
export type TaskState = "open" | "done" | "expired";

/**
 * Whether a task is open, done, or expired.
 *
 * A task with an expiry is open until the expiry and expired from it on, whatever its acknowledgements say: it stays
 * pinned for its whole span, and an acknowledgement only clears it as an item on other boards. A task without one is
 * done while an acknowledgement stands, and open otherwise, so an unacknowledge reopens it.
 *
 * @param acknowledgements - The task's own acknowledgement rows.
 * @param expiresAt - The task's expiry, ISO; null or absent when it never expires.
 * @param now - The reader's clock, as epoch milliseconds.
 */
export function taskState(
  acknowledgements: Iterable<AcknowledgementRecord>,
  expiresAt: string | null | undefined,
  now: number,
): TaskState {
  if (expiresAt) {
    return hasPassed(expiresAt, now) ? "expired" : "open";
  }
  return isStandingAcknowledged(foldAcknowledgements(acknowledgements)) ? "done" : "open";
}

/**
 * The instant a task expired, or null for one that never expires or whose expiry is still ahead.
 *
 * @param expiresAt - The task's expiry, ISO; null or absent when it never expires.
 * @param now - The reader's clock, as epoch milliseconds.
 */
export function expiredAt(expiresAt: string | null | undefined, now: number): string | null {
  return expiresAt && hasPassed(expiresAt, now) ? expiresAt : null;
}

/**
 * Whether an expiry is set and at or before `now`.
 */
function hasPassed(expiresAt: string | null | undefined, now: number): boolean {
  return !!expiresAt && DateTime.fromISO(expiresAt).toMillis() <= now;
}

/**
 * The pin in force: the latest by `(createdAt, id)`, or `null` when the task has none.
 *
 * Ties break on the id's byte order, as the acknowledgement fold's do, so every reader picks the same pin.
 */
export function pinOf(pins: Iterable<TaskPinBoardRecord>): TaskPinBoardRecord | null {
  let latest: TaskPinBoardRecord | null = null;
  for (const pin of pins) {
    if (latest === null || byLogOrder(pin, latest) > 0) {
      latest = pin;
    }
  }
  return latest;
}

/**
 * Whether an open task names the target, so the item counts as handled.
 *
 * A done task does not keep the item handled: only open tasks belong in the map, and once a task is done the item
 * falls back to its own acknowledgement log.
 *
 * @param open - Open tasks by {@link taskSubjectKey}.
 * @param subject - The item's subject kind.
 * @param targetId - The item's id within that kind.
 */
export function isHandledByTask(
  open: ReadonlyMap<string, readonly OpenTask[]>,
  subject: TaskSubjectRef["subject"],
  targetId: string,
): boolean {
  return (open.get(taskSubjectKey(subject, targetId))?.length ?? 0) > 0;
}

/**
 * The open tasks a board's shelf shows.
 *
 * The tasks pinned to the board, plus every task with no pin or with a pin naming a view the workspace no longer has,
 * so no open task is left on no shelf at all.
 *
 * @param open - The workspace's open tasks.
 * @param viewId - The board the shelf is on.
 * @param knownViewIds - Every view the workspace offers; a pin naming any other is orphaned.
 */
export function tasksForShelf(
  open: readonly OpenTask[],
  viewId: string,
  knownViewIds: ReadonlySet<string>,
): OpenTask[] {
  return open.filter(({ pin }) => pin === null || pin.viewId === viewId || !knownViewIds.has(pin.viewId));
}
